import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

//components
import Button from "@surgia/ui/components/button";
//queries
import { tenantQueries } from "@surgia/api/src/queryStore/shared/tenant";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { tenantQueries as adminTenantQueries } from "@surgia/api/src/queryStore/admin/tenant";
//constants
import { UpdateTenantProductPayload } from "@surgia/ui/dtos/request";
import { ProductConfig } from "../../../containers/productCard2";
//icons
import ProductCard2 from "../../../containers/productCard2";
import { useUpdateProduct } from "../../../hooks/useUpdateProduct";
import { useAssignParentProduct } from "../../../hooks/useAssignParentProduct";

interface CustomerViewProps {
  selectedProducts: string[];
  onPreviousStep: () => void;
  onFinish: () => void;
}

function CustomerView({
  selectedProducts,
  onPreviousStep,
  onFinish,
}: CustomerViewProps) {
  const { id: tenantId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Try to get currency from navigation state first (if navigated from TenantDetails)
  const currencyFromState = location.state?.currencyCode;
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set()
  );
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(
    new Set()
  );
  const [productConfigs, setProductConfigs] = useState<
    Record<string, ProductConfig>
  >({});
  const { t } = useTranslation();

  // Use the assign parent product hook
  const { assignParentProducts, isAssigning: isAssigningParentProduct } =
    useAssignParentProduct({
      tenantId: tenantId!,
    });

  // Fetch tenant data to get storefront_id
  const { data: tenantResponse } = useQuery({
    ...adminTenantQueries.queries.tenants({
      status: "ACTIVE",
      id: tenantId ? +tenantId : undefined,
    }),
  });

  // Get storefront_id from tenant response
  const storefront_id = tenantResponse?.data?.[0]?.storefront_id;

  // Fetch storefront currency - will use cache if already fetched in TenantDetails
  const { data: storefrontCurrencyData } = useQuery({
    ...lookupQueries.queries.storefrontCurrency({
      storefront_id: Number(storefront_id) || 0,
      is_base_currency: true,
    }),
    enabled: !!storefront_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get currency code - prefer from state, then cache
  const currencyCode = 
    currencyFromState || 
    storefrontCurrencyData?.[0]?.currency?.code;

  // Ref to store current tenant products data
  const tenantProductsDataRef = useRef<any[]>([]);
  // Fetch tenant products data
  const { data: tenantProductsResponse, isLoading } = useQuery({
    ...tenantQueries.queries.tenantProducts({
      tenant_id: +tenantId!,
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
      product_id: selectedProducts.join(","),
      currency_code: currencyCode,
    }),
    enabled: selectedProducts.length > 0,
    select: (data) => {
      // Find the parent product (first one with master_product_id = 0)
      const parentProduct =
        data?.data?.find(
          (product: any) => product.catalog_info.master_product_id === 0
        ) || data?.data[0];

      const parentProductId = parentProduct?.catalog_info?.id;
      const parentProductCurrentlyAssigned = parentProduct?.currently_assigned;

      tenantProductsDataRef.current = data?.data;

      return {
        total: data.total,
        data: data.data.map((tenantProduct: any) => {
          const isParent = tenantProduct.catalog_info?.id === parentProductId;
          return {
            parentProductCurrentlyAssigned: parentProductCurrentlyAssigned,
            id: tenantProduct.catalog_info?.id.toString(),
            name: tenantProduct.catalog_info.name,
            price: tenantProduct.catalog_info?.price || null,
            minQuantity: tenantProduct.minimum_quantity ?? null,
            maxQuantity: tenantProduct.maximum_quantity ?? null,
            customerProductId: tenantProduct.tenant_product_sku || "",
            customerProductName: tenantProduct.tenant_product_name || "",
            isParent: isParent,
            tenantProductId: tenantProduct?.id.toString(),
            secondId: tenantProduct?.id.toString(),
            parentProductId: parentProductId,
            customerView: tenantProduct?.customer_view,
            feature_values: tenantProduct?.catalog_info?.feature_values || [],
            currently_assigned: tenantProduct.currently_assigned,
            image: tenantProduct.catalog_info?.product_images?.[0]?.image_path,
            parentTenantProductId: tenantProduct.parent_tenant_product_id,
          };
        }),
      };
    },
  });
  // Use the new hook for updating products
  const { updateProduct, updateProductConfigs, isUpdating, updatingProductId } =
    useUpdateProduct({
      onProductConfigUpdate: (updatedConfigs) => {
        setProductConfigs(updatedConfigs);
      },
      tenantId: tenantId || "", // Add this
      parentProductId: tenantProductsResponse?.data?.[0]?.secondId || "", // Add this
    });
  // Mutation for updating customer view
  const updateCustomerViewMutation = useMutation({
    mutationFn: async ({
      productId,
      customerView,
    }: {
      productId: string;
      customerView: boolean;
    }) => {
      return productQueries.mutations
        .updateTenantProductCustomerView(productId)
        .mutationFn({ customerView });
    },
    onSuccess: () => {
      toast.success("Visibility updated successfully!");
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "product",
          "variations",
          tenantProductsResponse?.data?.[0]?.secondId,
          tenantId,
        ],
      });
    },
    onError: (error) => {
      toast.error("Failed to update visibility. Please try again.");
      console.error("Error updating visibility:", error);
    },
  });

  const toggleProductExpansion = (productId: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleVariantExpansion = (variantId: string) => {
    setExpandedVariants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variantId)) {
        newSet.delete(variantId);
      } else {
        newSet.add(variantId);
      }
      return newSet;
    });
  };

  const updateProductConfig = useCallback(
    (productId: string, field: keyof ProductConfig, value: any) => {
      setProductConfigs((prev: Record<string, ProductConfig>) => {
        const existingConfig = prev[productId];

        if (!existingConfig) {
          // Find the original product data
          const originalProduct = tenantProductsDataRef.current.find(
            (p: any) => p.catalog_info?.id.toString() === productId
          );

          if (originalProduct) {
            return {
              ...prev,
              [productId]: {
                id: originalProduct?.id.toString(),
                name: originalProduct.catalog_info.name,
                price: originalProduct.catalog_info?.price || null,
                minQuantity: originalProduct.minimum_quantity || null,
                maxQuantity: originalProduct.maximum_quantity || null,
                customerProductId: originalProduct.tenant_product_sku || "",
                customerProductName: originalProduct.tenant_product_name || "",
                isParent: originalProduct.catalog_info.master_product_id === 0,
                tenantProductId: originalProduct?.id.toString(),
                isVisible: true,
                hasInitialValues: true,
                hasChanges: true,
                tenantInfo: null, // Add this line to satisfy the interface
                [field]: value,
              },
            };
          }
        }

        // Update existing config
        return {
          ...prev,
          [productId]: {
            ...existingConfig,
            [field]: value,
            hasChanges: true,
          },
        };
      });
    },
    []
  );

  const isProductConfigValid = useCallback(
    (productId: string) => {
      const config = productConfigs[productId];
      if (!config) return false;

      return !!(
        config.price !== null &&
        config.minQuantity !== null &&
        config.maxQuantity !== null &&
        config.customerProductId &&
        config.customerProductId.trim() !== ""
      );
    },
    [productConfigs]
  );

  const handleSaveProduct = async (
    productId: string,
    tenantProductId?: string,
    formData?: any
  ) => {
    const config = formData || productConfigs[productId];

    const payload: UpdateTenantProductPayload = {
      minimumQuantity: config.minQuantity!,
      maximumQuantity: config.maxQuantity!,
      tenantProductName: config.customerProductName || "",
      tenantProductSku: config.customerProductId,
      productPrices: [
        {
          price: config.price!,
          currency: currencyCode!, // Use dynamic currency
        },
      ],
    };

    await updateProduct({
      tenantProductId: tenantProductId!,
      payload,
    });

    updateProductConfigs(productConfigs, tenantProductId!);
  };

  const handleAssignParentProduct = async (productId: string) => {
    try {
      await assignParentProducts({
        parentProductIds: [parseInt(productId)],
      });
    } catch (error) {
      console.error("Error assigning parent product:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 px-6 flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">Loading products...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Products List */}
      <div className="flex-1 space-y-4 bg-white p-6 rounded-lg">
        {tenantProductsResponse?.data?.map((product: any) => (
          <ProductCard2
            key={product?.id}
            product={product}
            expandedProducts={expandedProducts}
            expandedVariants={expandedVariants}
            productConfigs={productConfigs}
            updateProductConfig={updateProductConfig}
            handleSaveProduct={handleSaveProduct}
            toggleProductExpansion={toggleProductExpansion}
            toggleVariantExpansion={toggleVariantExpansion}
            isUpdatingProduct={isUpdating}
            updatingProductId={updatingProductId} // Pass the updatingProductId
            updateCustomerViewMutation={updateCustomerViewMutation}
            isProductConfigValid={isProductConfigValid}
            onAssignParentProduct={handleAssignParentProduct}
            isAssigningParentProduct={isAssigningParentProduct}
            showOnlyCurrentlyAssignedVariants={true}
            currencyCode={currencyCode}
          />
        ))}
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-row justify-end items-end px-8 py-3 pb-4 gap-3 w-full h-16 border-t border-[#E9EAEB] mt-auto">
        <div className="flex-1 flex items-center">
          <span className="text-sm text-gray-600">
            {selectedProducts.length}{" "}
            {t("admin.productAssignment.messages.productsSelected")}
          </span>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={onPreviousStep}>
            {t("admin.productAssignment.messages.previous")}
          </Button>
          <Button variant="primary" size="sm" onClick={onFinish}>
            {t("admin.productAssignment.messages.finish")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CustomerView;
